/**
 * Pure geometry for dropping face boxes on the upload queue's local previews.
 * Stored tag coordinates are normalized 0–1 floats relative to the IMAGE, not
 * the element that renders it — a preview drawn with object-fit can letterbox,
 * so clicks must first be mapped from the element box onto the rectangle the
 * image actually occupies within it.
 */

/** The subset of DOMRect this math needs, so tests never build real DOMRects. */
export interface RectLike {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** A face box in normalized image coordinates: x/y top-left corner, w/h size. */
export interface NormalizedBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Face boxes are fixed-size squares: this fraction of the image's shorter side. */
export const FACE_BOX_FRACTION = 0.18;

/**
 * Where an object-fit: contain image is actually drawn inside its element box.
 * With no letterboxing (or unknown natural dimensions, as when a preview
 * hasn't decoded) the drawn rectangle is the element box itself.
 */
export function drawnImageRect(
  element: RectLike,
  naturalWidth: number,
  naturalHeight: number,
): RectLike {
  if (element.width <= 0 || element.height <= 0 || naturalWidth <= 0 || naturalHeight <= 0) {
    return { ...element };
  }
  const scale = Math.min(element.width / naturalWidth, element.height / naturalHeight);
  const width = naturalWidth * scale;
  const height = naturalHeight * scale;
  return {
    left: element.left + (element.width - width) / 2,
    top: element.top + (element.height - height) / 2,
    width,
    height,
  };
}

/**
 * A fixed-size square centered on a click, normalized 0–1 against the drawn
 * image. Clicks that miss the drawn image (letterbox bars) return null; clicks
 * near an edge are clamped so the whole box stays within 0–1.
 */
export function clickToFaceBox(
  clickX: number,
  clickY: number,
  drawn: RectLike,
): NormalizedBox | null {
  if (drawn.width <= 0 || drawn.height <= 0) return null;
  if (
    clickX < drawn.left ||
    clickX > drawn.left + drawn.width ||
    clickY < drawn.top ||
    clickY > drawn.top + drawn.height
  ) {
    return null;
  }
  const side = FACE_BOX_FRACTION * Math.min(drawn.width, drawn.height);
  const w = Math.min(side / drawn.width, 1);
  const h = Math.min(side / drawn.height, 1);
  const x = clamp((clickX - drawn.left) / drawn.width - w / 2, 0, 1 - w);
  const y = clamp((clickY - drawn.top) / drawn.height - h / 2, 0, 1 - h);
  return { x, y, w, h };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
